import React, { useEffect, useState, useCallback } from "react";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { toast } from "sonner";
import { HeartPulse, Plus, Trash2, Ambulance } from "lucide-react";
import { MiemssBoard } from "@/components/MiemssBoard";
import { PamedicBoard } from "@/components/PamedicBoard";

const inp = "bg-[#0d0e11] border border-[#2C2D35] px-2.5 py-2 text-xs font-mono text-white outline-none focus:border-[#3B82F6] rounded-sm";
const H_COLOR = { open: "#22C55E", advisory: "#F59E0B", divert: "#EF4444", closed: "#7F1D1D" };
const R_COLOR = { available: "#22C55E", committed: "#F59E0B", out_of_service: "#EF4444" };

function Board({ title, icon: Icon, rows, statuses, colors, onStatus, onDelete, testPrefix }) {
  return (
    <div className="border border-[#2C2D35] rounded-sm" data-testid={`${testPrefix}-board`}>
      <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center gap-2">
        <Icon size={13} className="text-[#22D3EE]" /> {title}
      </div>
      {rows.length === 0 && <div className="p-3 font-mono text-[11px] text-[#6B7280]">Nothing tracked yet — add one below.</div>}
      {rows.map((r) => (
        <div key={r.id} className="flex items-center gap-2 px-3 py-2 border-b border-[#1E1F24] text-[11px] flex-wrap" data-testid={`${testPrefix}-row-${r.id}`}>
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: colors[r.status] || "#6B7280" }} />
          <span className="text-white font-semibold w-52 truncate">{r.name}</span>
          <select data-testid={`${testPrefix}-status-${r.id}`} className={`${inp} py-1`} value={r.status} onChange={(e) => onStatus(r, e.target.value)}>
            {statuses.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <span className="font-mono text-[10px] text-[#9CA3AF] flex-1 truncate">{r.detail}</span>
          <span className="font-mono text-[9px] text-[#6B7280]">{r.updated_by} · {(r.updated_at || "").slice(0, 16).replace("T", " ")}</span>
          <button data-testid={`${testPrefix}-del-${r.id}`} onClick={() => onDelete(r)} className="text-[#EF4444]"><Trash2 size={12} /></button>
        </div>))}
    </div>
  );
}

export default function ResourceStatus() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ kind: "hospital", name: "", status: "open", detail: "" });
  const load = useCallback(() => client.get("/resources").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const add = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    try { await client.post("/resources", form); toast.success("Added"); setForm({ ...form, name: "", detail: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const setStatus = async (r, status) => {
    try { await client.post("/resources", { id: r.id, kind: r.kind, status, detail: r.detail }); toast.success(`${r.name} → ${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Remove ${r.name} from the board?`)) return;
    try { await client.delete(`/resources/${r.id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const kindStatuses = form.kind === "hospital" ? (data?.hospital_statuses || []) : (data?.resource_statuses || []);
  return (
    <Layout>
      <PageHeader title="Resource & Hospital Status" sub="ER diversion board · specialty resource availability · auto-refresh 30s" icon={HeartPulse} />
      <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="resources-page">
        <MiemssBoard />
        <PamedicBoard />
        <Board title="HOSPITAL / ER DIVERSION BOARD" icon={HeartPulse} rows={data?.hospitals || []}
          statuses={data?.hospital_statuses || []} colors={H_COLOR} onStatus={setStatus} onDelete={del} testPrefix="hospital" />
        <Board title="SPECIALTY RESOURCES (air medical · K9 · SWAT · heavy rescue …)" icon={Ambulance} rows={data?.resources || []}
          statuses={data?.resource_statuses || []} colors={R_COLOR} onStatus={setStatus} onDelete={del} testPrefix="resource" />

        <div className="border border-[#2C2D35] rounded-sm">
          <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold">ADD TO BOARD</div>
          <div className="p-3 flex flex-wrap gap-2 items-end">
            <div><label className="block mb-0.5 font-mono text-[9px] text-[#6B7280] uppercase">Kind</label>
              <select data-testid="res-kind" className={inp} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value, status: e.target.value === "hospital" ? "open" : "available" })}>
                <option value="hospital">hospital</option><option value="resource">resource</option>
              </select></div>
            <div><label className="block mb-0.5 font-mono text-[9px] text-[#6B7280] uppercase">Name</label><input data-testid="res-name" className={`${inp} w-64`} value={form.name} placeholder={form.kind === "hospital" ? "UPMC Mercy ER" : "Air Medical 1"} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><label className="block mb-0.5 font-mono text-[9px] text-[#6B7280] uppercase">Status</label>
              <select data-testid="res-status" className={inp} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {kindStatuses.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select></div>
            <div className="flex-1 min-w-[160px]"><label className="block mb-0.5 font-mono text-[9px] text-[#6B7280] uppercase">Detail</label><input data-testid="res-detail" className={`${inp} w-full`} value={form.detail} placeholder="Notes / capability / ETA…" onChange={(e) => setForm({ ...form, detail: e.target.value })} /></div>
            <button data-testid="res-add" onClick={add} className="flex items-center gap-1 px-3 py-2 text-[10px] font-head font-bold bg-[#22D3EE] text-black rounded-sm"><Plus size={12} /> ADD</button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
